import React, { useState } from 'react'
import { Navigation, NavigationOff, Loader2 } from 'lucide-react'
import mapboxgl from 'mapbox-gl'

interface GeolocationControlProps {
  map: mapboxgl.Map | null
  className?: string
}

export function GeolocationControl({ map, className = '' }: GeolocationControlProps) {
  const [isLocating, setIsLocating] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [userMarker, setUserMarker] = useState<mapboxgl.Marker | null>(null)

  const handleFindLocation = () => {
    if (!map || !navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser')
      return
    }

    setIsLocating(true)
    setLocationError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        
        // Fly to user's location
        map.flyTo({
          center: [longitude, latitude],
          zoom: 14,
          duration: 2000,
          essential: true
        })

        // Remove existing marker if any
        if (userMarker) {
          userMarker.remove()
        }

        // Create a custom marker element
        const el = document.createElement('div')
        el.className = 'user-location-marker'
        el.style.width = '20px'
        el.style.height = '20px'
        el.style.borderRadius = '50%'
        el.style.backgroundColor = '#3b82f6'
        el.style.border = '3px solid white'
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)'
        
        // Add pulsing animation
        const pulseEl = document.createElement('div')
        pulseEl.className = 'pulse'
        pulseEl.style.position = 'absolute'
        pulseEl.style.width = '100%'
        pulseEl.style.height = '100%'
        pulseEl.style.borderRadius = '50%'
        pulseEl.style.backgroundColor = '#3b82f6'
        pulseEl.style.opacity = '0.5'
        pulseEl.style.animation = 'pulse 2s infinite'
        el.appendChild(pulseEl)

        // Add CSS animation
        const style = document.createElement('style')
        style.textContent = `
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 0.5;
            }
            50% {
              transform: scale(1.5);
              opacity: 0.2;
            }
            100% {
              transform: scale(1);
              opacity: 0.5;
            }
          }
        `
        document.head.appendChild(style)

        // Create and add the marker
        const marker = new mapboxgl.Marker(el)
          .setLngLat([longitude, latitude])
          .addTo(map)

        setUserMarker(marker)
        setIsLocating(false)
      },
      (error) => {
        setIsLocating(false)
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied')
            break
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable')
            break
          case error.TIMEOUT:
            setLocationError('Location request timed out')
            break
          default:
            setLocationError('An unknown error occurred')
            break
        }
        // Clear error after 3 seconds
        setTimeout(() => setLocationError(null), 3000)
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    )
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleFindLocation}
        disabled={isLocating}
        className="text-gray-200 hover:bg-gray-700/50 transition-colors flex items-center justify-center w-full h-[32px] disabled:opacity-50 disabled:cursor-not-allowed"
        title={locationError || "Find my location"}
      >
        {isLocating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : locationError ? (
          <NavigationOff className="w-4 h-4 text-red-400" />
        ) : (
          <Navigation className="w-4 h-4" />
        )}
      </button>
      
      {/* Error tooltip */}
      {locationError && (
        <div className="absolute bottom-full mb-2 right-0 bg-gray-800/95 text-red-400 text-xs px-2 py-1 rounded whitespace-nowrap">
          {locationError}
        </div>
      )}
    </div>
  )
}